/**
 * Servicio de chat para el chatbot del libro
 * Usa datos reales de la API: bookData (título + lecciones) y lessonUMAData (contenido de la UMA)
 */

import { openAICall, getDocumentoIA, PRODUCTO_NIA } from '../../services/api';
import { ROLES } from '../roles';

// ---------------------------------------------------------------------------
// Utilidades
// ---------------------------------------------------------------------------

function extractTextFromHTML(html) {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}

function extractKeywords(query) {
  const stopWords = ['como', 'que', 'cual', 'cuales', 'quien', 'quienes', 'donde', 'cuando',
    'por', 'para', 'con', 'sin', 'sobre', 'entre', 'del', 'los', 'las', 'una', 'uno',
    'unos', 'unas', 'este', 'esta', 'estos', 'estas', 'ese', 'esa', 'esos', 'esas',
    'son', 'es', 'fue', 'ser', 'sido', 'siendo', 'tiene', 'tienen', 'hay', 'haber',
    'puede', 'pueden', 'debe', 'deben', 'hace', 'hacen', 'libro'];
  return query.toLowerCase()
    .replace(/[¿?¡!.,;:]/g, '')
    .split(/\s+/)
    .filter(w => w.length > 2 && !stopWords.includes(w));
}

// Mensajes de fallback que sendChatMessage devuelve como `content` cuando la
// llamada a OpenAI falla. No son un resumen/respuesta real: si se detectan en
// una acción de audio/podcast, no deben enviarse al TTS (se leerían en voz alta
// como si fueran el resumen).
const CHATBOT_ERROR_FALLBACKS = [
  'Lo siento, tuve un problema técnico. ¿Puedes intentar de nuevo?',
  'Lo siento, estoy teniendo dificultades. Por favor, intenta de nuevo.',
];

export function isChatbotErrorFallback(content) {
  return CHATBOT_ERROR_FALLBACKS.includes((content || '').trim());
}

// Aperturas típicas de una respuesta en la que el modelo rechaza la tarea en vez de
// resolverla ("No puedo darte un resumen de cada lección…", "Lo siento, no tengo…").
// No son contenido: si se guardan en caché quedan fijas para todos los usuarios de
// ese rol en ese libro, que es como una falla ocasional se vuelve permanente.
const PATRONES_RESPUESTA_RECHAZADA = [
  /^(lo siento[,.]?\s*|disculpa[,.]?\s*)?no (puedo|podr[ií]a|me es posible|estoy en condiciones)\b/i,
  /^(lo siento[,.]?\s*|disculpa[,.]?\s*)?no (tengo|cuento con|dispongo de)\s+(acceso|informaci[oó]n|suficiente|contenido|datos)/i,
  /^(lo siento|disculpa|perd[oó]n)[,.]/i,
  // "Para proporcionarte un resumen… necesitaría que me indiques el libro" / "Indícame la lección"
  /^(para|a fin de)\b[^.]{0,140}\b(necesito|necesitar[ií]a|requiero|requerir[ií]a)\b/i,
  /^(por favor[,.]?\s*)?(ind[ií]came|espec[ií]fica|selecciona|elige)\b/i,
];

/**
 * True cuando la respuesta del modelo no trae contenido útil (rechazo o vacío).
 * Se evalúa solo el arranque: un texto válido puede mencionar limitaciones al final.
 * @param {string} content
 * @returns {boolean}
 */
export function esRespuestaRechazada(content) {
  const texto = (content || '').trim();
  if (!texto) return true;
  const inicio = texto.slice(0, 200);
  return PATRONES_RESPUESTA_RECHAZADA.some(patron => patron.test(inicio));
}

// 'text' | 'audio' | 'podcast' | 'diagram' | 'image' | 'structured'
export const RESPONSE_TYPES = {
  TEXT: 'text',
  AUDIO: 'audio',
  PODCAST: 'podcast',
  DIAGRAM: 'diagram',
  IMAGE: 'image',
  STRUCTURED: 'structured',
};

/**
 * Resuelve el tipo de salida de una Quick Action. Prioriza el campo explícito
 * `outputType` (acciones creadas desde el Gestor) y cae a los flags legacy
 * (isAudio/isPodcast/isDiagram/isImage) para acciones hardcodeadas antiguas.
 * @param {Object} action
 * @returns {'text'|'audio'|'podcast'|'diagram'|'image'|'structured'}
 */
export function getActionOutputType(action = {}) {
  if (action.outputType) return action.outputType;
  if (action.isPodcast) return 'podcast';
  if (action.isAudio) return 'audio';
  if (action.isDiagram) return 'diagram';
  if (action.isImage) return 'image';
  return 'text';
}

/**
 * Resuelve el subtipo estructurado de una Quick Action (solo aplica cuando
 * outputType === 'structured'). Acepta el campo nuevo `structuredType` y el
 * alias `schemaType` por compatibilidad. Devuelve '' si no está definido.
 * @param {Object} action
 * @returns {''|'quiz'|'activity'|'rubric'|'flashcards'|'lessonPlan'|string}
 */
export function getStructuredType(action = {}) {
  return action.structuredType || action.schemaType || '';
}

// ---------------------------------------------------------------------------
// Modelos disponibles — fuente única de verdad para ChatbotLibro y chatService
// ---------------------------------------------------------------------------

// El chatbot va por /lia-api/llamar-openai, que es chat completions. El backend
// valida el modelo contra la allowlist de ese ámbito —hoy gpt-4.1-mini y gpt-4.1—
// y lo que no esté en ella cae al modelo por defecto sin avisar al cliente. Los
// gpt-5.6 quedan fuera a propósito: razonan, y chat completions los rechaza.
//
// gpt-4o y gpt-4o-mini salieron de la lista; gpt-4.1-mini los sustituye a los dos.
// bookSummary sube por el resumen de libro: 4000 tokens no alcanzan ni para 40
// lecciones. Techo de salida de la API: gpt-4.1-mini y gpt-4.1, 32768 los dos.
export const MODELS = [
  {
    value: 'gpt-4.1-mini',
    label: 'Respuesta rápida',
    desc: 'Ideal para preguntas simples y consultas rápidas. Menor tiempo de espera.',
    maxTokens: { text: 1000, audio: 1500, podcast: 2000, diagram: 1500, bookSummary: 12000, structured: 2000, presentation: 3500 },
  },
  {
    value: 'gpt-4.1',
    label: 'Respuesta avanzada',
    desc: 'Mayor profundidad y precisión. Ideal para análisis y explicaciones complejas.',
    maxTokens: { text: 1500, audio: 2000, podcast: 3000, diagram: 2000, bookSummary: 16000, structured: 3000, presentation: 4500 },
  },
];

// Modelo con el que arranca el chatbot: es "el del chatbot" al que caen las Quick
// Actions en Automático. El ALUMNO se queda siempre con este (no ve el selector);
// el PROFESOR puede cambiarlo durante su sesión desde el chatbot.
export const DEFAULT_MODEL = 'gpt-4.1-mini';

/**
 * Modelo con el que se ejecuta una Quick Action. Manda el que traiga configurado
 * la acción desde el Gestor; si no trae ninguno (o trae uno que ya no existe),
 * se usa el del chatbot, que es el comportamiento previo a este campo.
 * @param {Object} action
 * @param {string} modelPorDefecto - el modelo seleccionado en el chatbot
 * @returns {string}
 */
export function getActionModel(action = {}, modelPorDefecto = DEFAULT_MODEL) {
  return MODELS.some(m => m.value === action.model) ? action.model : modelPorDefecto;
}

function getMaxTokens(model, responseType = 'text') {
  const m = MODELS.find(m => m.value === model) ?? MODELS[0];
  return m.maxTokens[responseType] ?? m.maxTokens.text;
}

/**
 * Clave de tope de salida para una Quick Action. Las presentaciones piden la suya:
 * con el tope de 'structured' (2000/3000) el JSON de 10 diapositivas con notas del
 * orador se corta a media diapositiva, y una respuesta cortada no se puede parsear
 * —cae al render de texto crudo— además de que no se cachea.
 * @param {'text'|'audio'|'podcast'|'diagram'|'image'|'structured'} responseType
 * @param {Object} action
 * @returns {string}
 */
function getMaxTokensKey(responseType, action = {}) {
  if (responseType === 'structured' && getStructuredType(action) === 'presentation') {
    return 'presentation';
  }
  return responseType;
}

// ---------------------------------------------------------------------------
// Extracción de contenido desde la UMA real (API)
// ---------------------------------------------------------------------------

/**
 * Extrae el contexto relevante de una UMA obtenida desde la API.
 * Soporta filtrado por resumen, profesor, alumno y búsqueda por keywords.
 * @param {Object} uma - UMA con umaBloques[]
 * @param {string} query
 * @returns {{ found: boolean, data: Array }}
 */
function extractContextFromUMA(uma, query) {
  if (!uma || !uma.umaBloques) return { found: false };

  const leccionNombre = uma.descripcion || uma.titulo || 'Lección';
  const queryLower = query.toLowerCase();
  const results = [];

  const esResumen = queryLower.includes('resumen');
  const esProfesor = queryLower.includes('profesor') || queryLower.includes('docente') || queryLower.includes('maestro');
  const esAlumno = queryLower.includes('alumno') || queryLower.includes('estudiante');
  const keywords = extractKeywords(query);

  for (const bloque of uma.umaBloques) {
    const tipoBloque = bloque.bloque?.titulo || 'Contenido';
    const esModoProfesor = bloque.esModoProfesor || false;

    if (esProfesor && !esModoProfesor) continue;
    if (esAlumno && esModoProfesor) continue;

    if (bloque.texto) {
      const textoLimpio = extractTextFromHTML(bloque.texto);
      if (textoLimpio.length > 10) {
        // Incluir si es resumen (todo), o si coincide keyword, o si es los primeros bloques
        const matchesKeyword = keywords.length === 0 || keywords.some(kw => textoLimpio.toLowerCase().includes(kw));
        if (esResumen || matchesKeyword || results.length < 3) {
          results.push({
            leccion: leccionNombre,
            tipoBloque,
            contenido: textoLimpio.substring(0, 800),
            esModoProfesor,
            tieneImagen: !!bloque.imagen,
            tieneVideo: !!bloque.path
          });
        }
      }
    }
  }

  if (results.length === 0) return { found: false };

  if (esResumen) {
    const contenidoCombinado = results.map(r => r.contenido).join('\n\n');
    return {
      found: true,
      data: [{
        leccion: leccionNombre,
        tipoBloque: 'Resumen de lección',
        contenido: contenidoCombinado.substring(0, 2500),
        objetivo: uma.objetivosAprendizaje || '',
        tiposBloques: [...new Set(results.map(r => r.tipoBloque))]
      }]
    };
  }

  return { found: true, data: results.slice(0, 8) };
}

/**
 * Genera un string de contexto para prompts de imagen/diagrama a partir de la UMA real.
 * @param {Object} uma
 * @returns {string}
 */
function getContentFromUMA(uma) {
  if (!uma) return 'lección educativa';
  const nombre = uma.descripcion || uma.titulo || 'Lección';
  const textos = (uma.umaBloques || [])
    .filter(b => b.texto)
    .map(b => extractTextFromHTML(b.texto))
    .slice(0, 4)
    .join(' ');
  return `${nombre}. ${textos}`.substring(0, 600);
}

/**
 * Genera un string de contexto para prompts de imagen/diagrama a partir de bookData.
 * @param {{ titulo: string, lecciones: string[] }} bookData
 * @returns {string}
 */
export function getBookContentForImage(bookData) {
  if (!bookData) return 'libro educativo';
  const temas = bookData.lecciones.slice(0, 8).join(', ');
  return `${bookData.titulo}. Temas: ${temas}`.substring(0, 600);
}

// ---------------------------------------------------------------------------
// System prompts — un único generador parametrizado
// ---------------------------------------------------------------------------

const FORMAT_BLOCKS = {
  text: `FORMATO DE RESPUESTA (usa Markdown estándar):
- Títulos principales con ## y subtítulos con ###
- Texto importante en **negrita**
- Listas con viñetas usando - y listas numeradas con 1.
- Tablas con | cuando necesites comparar datos
- Empieza directamente con el contenido, sin frases como "Claro", "Aquí tienes" o "Por supuesto"
`,
  audio: `INSTRUCCIONES PARA AUDIO:
- Inicia directamente con el contenido, sin saludos ni frases de introducción
- Usa oraciones claras y fluidas, aptas para ser escuchadas en voz alta
- Evita Markdown (*, #, -, tablas) que no suena bien en audio
`,
  podcast: `INSTRUCCIONES PARA PODCAST:
- Estructura el contenido como un diálogo entre dos personas
- Usa EXACTAMENTE estos marcadores al inicio de cada línea: "Anfitrión:" para el presentador y "Invitado:" para el experto
- Alterna entre ambos de forma natural y conversacional
- Evita Markdown, listas y encabezados
- Inicia directamente con el diálogo, sin introducciones
`,
  structured: `FORMATO DE RESPUESTA:
- Responde ÚNICAMENTE con un objeto JSON válido.
- No uses Markdown, encabezados, listas, ni bloques de código (\`\`\`).
- No incluyas ningún texto antes ni después del JSON.
`,
};

const ROLE_INSTRUCTIONS = {
  [ROLES.alumno]: `ROL: Asistente para estudiantes
- Explica los conceptos de forma clara y sencilla, adaptada al nivel educativo
- Usa ejemplos prácticos y cotidianos para facilitar la comprensión
- Motiva al estudiante y refuerza su aprendizaje positivamente
- Si el estudiante te pide la respuesta de una evaluación o actividad, guía su razonamiento en vez de dársela. Esta regla NO te impide resumir, explicar ni describir el contenido de la lección o del libro, aunque ese contenido venga en forma de actividades`,

  [ROLES.profesor]: `ROL: Asistente para profesores
- Proporciona información pedagógica y didáctica detallada
- Menciona objetivos de aprendizaje y competencias a desarrollar
- Ofrece estrategias de enseñanza y evaluación cuando sea relevante`,

  [ROLES.padre]: `ROL: Asistente para padres y tutores
- Explica el contenido educativo en términos accesibles para acompañar el aprendizaje en casa
- Sugiere cómo apoyar al estudiante con el tema desde casa
- Evita tecnicismos pedagógicos innecesarios`,

  [ROLES.coordinador]: `ROL: Asistente para coordinación académica
- Proporciona información sobre la estructura y organización del contenido
- Menciona objetivos de aprendizaje y su relación con la planeación académica`,

  [ROLES.plantel]: `ROL: Asistente para directivos de plantel
- Ofrece una visión general del programa educativo y sus objetivos
- Resume la metodología y enfoque educativo del material`,

  [ROLES.corporativo]: `ROL: Asistente para directivos corporativos
- Ofrece una visión general del programa educativo y sus objetivos a nivel institucional
- Resume la metodología y enfoque educativo del material`,

  [ROLES.admin]: `ROL: Asistente para administradores
- Proporciona información sobre la estructura y organización del contenido
- Detalla los tipos de recursos disponibles`,

  [ROLES.operativo]: `ROL: Asistente para administración operativa
- Proporciona información sobre la estructura y organización del contenido
- Detalla los tipos de recursos disponibles`,

  general: `ROL: Asistente educativo
- Adapta la respuesta al contexto de la pregunta
- Usa un tono profesional pero accesible`,
};

/**
 * Genera el system prompt según el contexto activo y el tipo de respuesta.
 * @param {string} userRole - valor de ROLES (src/components/utils/roles.js) o 'general' como fallback
 * @param {'lesson'|'book'|'generic'} contextType
 * @param {Object} contextData - { lessonName, uma } para lesson; { bookData } para book
 * @param {'text'|'audio'|'podcast'} responseType
 */
/**
 * @param {string|null} editorInstruction - Prompt del director/editorial. Cuando está presente
 *   se coloca como TAREA PRINCIPAL con máxima prioridad, desplazando las instrucciones genéricas.
 */
function buildSystemPrompt(userRole = 'general', contextType = 'generic', contextData = {}, responseType = 'text', editorInstruction = null) {
  const formatBlock = FORMAT_BLOCKS[responseType] || FORMAT_BLOCKS.text;
  const roleText = (ROLE_INSTRUCTIONS[userRole] || ROLE_INSTRUCTIONS.general).trim();

  let contextSection = '';
  let defaultInstructions = '';

  if (contextType === 'lesson') {
    const { lessonName, uma } = contextData;
    const objetivo = uma?.objetivosAprendizaje ? `\nOBJETIVO: ${uma.objetivosAprendizaje}` : '';
    contextSection = `LECCIÓN ACTIVA: ${lessonName}${objetivo}`;
    defaultInstructions = `1. Responde ÚNICAMENTE con base en el CONTENIDO proporcionado de esta lección.
2. Responde de forma BREVE y directa: solo lo que se pregunta. NO reproduzcas todo el contenido ni la metadata (título, identificador, campos curriculares, objetivos, autores…) a menos que se te pida explícitamente.
3. Si lo que se pregunta SÍ está en el contenido (de forma textual o expresado con otras palabras), respóndelo con normalidad. Solo si NO está de ninguna forma, dilo claramente ("No encuentro eso en esta lección"); no lo inventes.
4. La lección activa es "${lessonName}"; no atribuyas el contenido a otras lecciones.
5. No inventes nombres de secciones, apartados ni títulos que no aparezcan en el contenido.
6. Si el mensaje del usuario contiene lenguaje ofensivo, altisonante o inapropiado, no respondas a la petición: pide cortésmente mantener un lenguaje respetuoso y reencauza hacia el contenido.`;
  } else if (contextType === 'book') {
    const { bookData } = contextData;
    const leccionesList = bookData.lecciones.length > 0
      ? `\n\nLECCIONES:\n${bookData.lecciones.map((l, i) => `${i + 1}. ${l}`).join('\n')}`
      : '';
    contextSection = `LIBRO ACTIVO: ${bookData.titulo}${leccionesList}`;
    defaultInstructions = `1. Cada lección viene delimitada por marcadores "===== INICIO LECCIÓN: <título> =====" y "===== FIN LECCIÓN: <título> =====". Esos marcadores y la metadata (título, identificador, campos curriculares, objetivos, autores, idioma, recursos…) son SOLO referencia interna: NUNCA los copies ni los muestres en tu respuesta.
2. Para decir en qué lección está algo, identifica el bloque cuyo contenido lo trate o lo contenga (de forma textual o expresado con otras palabras) y reporta EXACTAMENTE ese <título>. No lo atribuyas a una lección solo por parecido temático si su bloque no lo aborda.
3. Responde de forma BREVE y directa: solo lo que se pregunta. NO reproduzcas el contenido completo de la lección a menos que se te pida explícitamente.
4. Solo si NINGÚN bloque trata sobre lo que se pregunta, dilo claramente ("No encuentro eso en el contenido"). NO adivines.
5. No inventes nombres de lecciones, secciones ni datos que no estén en el contenido.
6. Si el mensaje del usuario contiene lenguaje ofensivo, altisonante o inapropiado, no respondas a la petición: pide cortésmente mantener un lenguaje respetuoso y reencauza hacia el contenido.`;
  } else if (editorInstruction) {
    // Quick Action: el libro o la lección ya vienen resueltos dentro del CONTENIDO
    // DISPONIBLE. Aquí NO va la instrucción de "selecciona un libro o lección": el
    // modelo la tomaba como una salida válida y contestaba "pregúntame algo más
    // específico" en vez de ejecutar la tarea.
    defaultInstructions = `1. Ejecuta la TAREA PRINCIPAL con el contenido disponible; ese contenido ya es el del libro o la lección que el usuario tiene abierto.
2. NUNCA pidas al usuario que seleccione un libro o una lección, ni le sugieras que pregunte algo más específico.
3. Si el contenido de alguna parte es escaso, resuelve la tarea con lo que sí haya y anótalo al final en una línea; no rechaces la tarea completa.`;
  } else {
    defaultInstructions = `1. Responde de forma profesional sobre contenido educativo
2. Indica al usuario que seleccione un libro o lección para obtener información específica`;
  }

  // El prompt del director reemplaza las instrucciones genéricas y toma máxima prioridad
  const editorBlock = editorInstruction
    ? `\nTAREA PRINCIPAL (sigue esta instrucción exactamente, usando el contenido disponible como base):\n${editorInstruction}\n`
    : '';

  const instructionsBlock = defaultInstructions
    ? `\nINSTRUCCIONES GENERALES:\n${defaultInstructions}`
    : '';

  return `${formatBlock}
Eres un asistente educativo de Nimbus GED.
${editorBlock}
${contextSection ? `${contextSection}\n` : ''}
${roleText}
${instructionsBlock}`.trim();
}

// Alias para compatibilidad con imports externos
export { buildSystemPrompt as generateSystemPrompt };

// ---------------------------------------------------------------------------
// PromptBuilder genérico para Quick Actions
// ---------------------------------------------------------------------------

/**
 * Selecciona el prompt editorial configurado en la Quick Action según la vista activa.
 * Soporta acciones hardcodeadas y dinámicas desde BD.
 * @param {Object} action
 * @param {'lesson'|'book'} tipoVista
 * @returns {string}
 */
function getEditorInstruction(action = {}, tipoVista = 'lesson') {
  // La BD ya está normalizada: solo existen queryLesson y queryBook.
  // action.query es el fallback que ChatbotLibro genera al filtrar por contexto.
  const instruction = tipoVista === 'book'
    ? action.queryBook || action.query
    : action.queryLesson || action.query;

  return typeof instruction === 'string' ? instruction.trim() : '';
}

// ¿La instrucción editorial pide una salida POR CADA lección? Solo en ese caso el
// resumen de libro puede partirse en lotes: una acción que pide una salida única
// (una conclusión, una rúbrica del libro) saldría repetida una vez por lote.
const PATRON_TAREA_POR_LECCION = /(cada|todas)\s+(una\s+de\s+)?(las\s+)?lecci[oó]n(es)?|por\s+lecci[oó]n|lecci[oó]n\s+por\s+lecci[oó]n/i;

function esTareaPorLeccion(editorInstruction = '') {
  return PATRON_TAREA_POR_LECCION.test(editorInstruction || '');
}

const OUTPUT_RULES = {
  text: `REGLAS PARA TEXTO:
- Usa únicamente el contenido proporcionado.
- No inventes información.
- Si el contenido es escaso, resuelve la tarea con lo que haya y anótalo en una línea al final; no rechaces la tarea.
- Responde en Markdown.
- Usa títulos y listas cuando ayuden a la comprensión.
- Empieza directamente con el contenido.`,

  audio: `REGLAS PARA AUDIO:
- Usa únicamente el contenido proporcionado.
- No inventes información.
- Escribe texto natural para ser escuchado en voz alta.
- No uses Markdown.
- No uses tablas.
- Evita listas largas.
- Usa oraciones claras y fluidas.
- Empieza directamente con el contenido.`,

  podcast: `REGLAS PARA PODCAST:
- Usa únicamente el contenido proporcionado.
- Genera un guion conversacional.
- Usa exactamente los marcadores "Anfitrión:" e "Invitado:".
- Alterna entre ambos de forma natural.
- No uses Markdown.
- No inventes información.
- Empieza directamente con el diálogo.`,

  image: `REGLAS PARA IMAGEN:
- Usa únicamente el contenido proporcionado.
- Genera una descripción visual clara basada en el contenido.
- Describe elementos principales, estilo visual, composición y ambiente.
- Evita pedir texto largo dentro de la imagen.
- No inventes elementos ajenos al contenido.`,

  diagram: `REGLAS PARA DIAGRAMA:
- Usa únicamente el contenido proporcionado.
- Organiza conceptos principales y secundarios.
- Evita textos largos.
- Mantén etiquetas breves.
- No inventes conceptos que no estén en el contenido.`,

  structured: `REGLAS PARA SALIDA ESTRUCTURADA:
- Usa únicamente el contenido proporcionado.
- No inventes información.
- Responde únicamente con JSON válido.
- No incluyas Markdown.
- No incluyas explicaciones fuera del JSON.
- Si el contenido no es suficiente, incluye un campo "advertencias".
- Mantén una estructura clara, consistente y fácil de procesar.`,
};

/**
 * Devuelve las reglas de salida para el tipo de respuesta solicitado.
 * Si tipoVista es 'book', agrega notas adicionales sobre contenido multi-lección.
 * @param {'text'|'audio'|'podcast'|'image'|'diagram'} responseType
 * @param {'lesson'|'book'} tipoVista
 * @returns {string}
 */
function getOutputRules(responseType = 'text', tipoVista = 'lesson') {
  const rules = OUTPUT_RULES[responseType] || OUTPUT_RULES.text;

  if (tipoVista === 'book') {
    return `${rules}

NOTAS PARA LIBRO:
- El contenido viene separado por lecciones: cada lección empieza con un encabezado "## <título>".
- Cada lección viene con su número ("## 7. <nombre>") y, debajo, la línea "Agrupador: ..." con la unidad a la que pertenece. Varias lecciones pueden llamarse EXACTAMENTE igual: son distintas y el número es lo que las separa.
- Si la tarea pide algo por lección, cúbrelas TODAS en el mismo orden: una entrada por cada lección, conservando su número. No agrupes varias lecciones en una sola entrada, no fusiones las que compartan nombre y no omitas ninguna.
- Organiza la respuesta por agrupador: escribe el nombre del agrupador como encabezado de sección con "## " y SIN número, y debajo cada una de sus lecciones con "### <número>. <nombre de la lección>". Si la ruta trae varios niveles, usa el que agrupe esas lecciones con más sentido para quien lee (normalmente la unidad o el proyecto, no el libro completo).
- Repite el encabezado del agrupador solo cuando cambie. Si el bloque de lecciones que recibes empieza a media unidad, escribe su encabezado antes de continuar.
- Genera una respuesta integrada del libro (sin ir lección por lección) solo cuando la tarea lo pida así explícitamente.`;
  }

  return rules;
}

// ---------------------------------------------------------------------------
// Schemas para Quick Actions estructuradas (outputType === 'structured')
// ---------------------------------------------------------------------------

// Cada schema describe la estructura JSON exacta que el modelo debe devolver.
// Son plantillas instructivas (no JSON parseable): los valores indican el tipo.
const STRUCTURED_OUTPUT_SCHEMAS = {
  quiz: `{
  "tipo": "quiz",
  "titulo": "string",
  "instrucciones": "string",
  "preguntas": [
    {
      "id": "string",
      "tipo": "opcion_multiple | verdadero_falso | respuesta_corta",
      "pregunta": "string",
      "opciones": ["string"],
      "respuestaCorrecta": "string",
      "retroalimentacion": "string"
    }
  ],
  "advertencias": []
}`,

  activity: `{
  "tipo": "activity",
  "titulo": "string",
  "objetivo": "string",
  "duracionSugerida": "string",
  "materiales": ["string"],
  "instrucciones": ["string"],
  "criteriosExito": ["string"],
  "variantes": ["string"],
  "advertencias": []
}`,

  rubric: `{
  "tipo": "rubric",
  "titulo": "string",
  "criterios": [
    {
      "criterio": "string",
      "niveles": [
        {
          "nivel": "Excelente | Bueno | Suficiente | Requiere apoyo",
          "descripcion": "string",
          "puntaje": number
        }
      ]
    }
  ],
  "puntajeTotal": number,
  "advertencias": []
}`,

  flashcards: `{
  "tipo": "flashcards",
  "titulo": "string",
  "tarjetas": [
    {
      "frente": "string",
      "reverso": "string",
      "pista": "string"
    }
  ],
  "advertencias": []
}`,

  lessonPlan: `{
  "tipo": "lessonPlan",
  "titulo": "string",
  "objetivo": "string",
  "inicio": "string",
  "desarrollo": "string",
  "cierre": "string",
  "evaluacion": "string",
  "materiales": ["string"],
  "tiempoEstimado": "string",
  "advertencias": []
}`,

  presentation: `{
  "tipo": "presentation",
  "titulo": "string",
  "subtitulo": "string",
  "diapositivas": [
    {
      "titulo": "string",
      "puntos": ["string"],
      "notas": "string"
    }
  ],
  "advertencias": []
}`,

  // Fallback genérico cuando outputType es 'structured' pero falta structuredType.
  generic: `{
  "tipo": "structured",
  "titulo": "string",
  "elementos": [],
  "advertencias": []
}`,
};

// Reglas adicionales para subtipos que necesitan más que la estructura JSON.
// El tope de 10 diapositivas también se aplica del lado del cliente (el visor y
// el .pptx recortan), pero pedirlo aquí evita gastar tokens en lo que se va a tirar.
const STRUCTURED_EXTRA_RULES = {
  presentation: `REGLAS DE LA PRESENTACIÓN:
- Máximo 10 diapositivas en "diapositivas". Nunca devuelvas más: si el contenido da para más, agrupa y quédate con lo esencial.
- Entre 3 y 5 puntos por diapositiva, de una sola línea cada uno (máximo 140 caracteres) y sin viñetas, guiones ni numeración al inicio.
- "notas" son las notas del orador: una o dos frases con lo que diría quien expone, sin repetir los puntos al pie de la letra.
- "titulo" es el de la presentación completa y "subtitulo" una línea de contexto (tema, lección o libro); la portada se arma con ellos, así que no los repitas como primera diapositiva.`,
};

/**
 * Devuelve las reglas de schema para un structuredType. Incluye la estructura
 * JSON esperada y refuerza las reglas de salida JSON. Si el structuredType no
 * está soportado, usa el schema genérico con "tipo": "structured".
 * @param {string} structuredType
 * @returns {string}
 */
function getStructuredSchemaRules(structuredType) {
  const isKnown = Boolean(STRUCTURED_OUTPUT_SCHEMAS[structuredType]);
  const schema = isKnown ? STRUCTURED_OUTPUT_SCHEMAS[structuredType] : STRUCTURED_OUTPUT_SCHEMAS.generic;
  const tipoEsperado = isKnown ? structuredType : 'structured';

  return `ESTRUCTURA JSON OBLIGATORIA (${tipoEsperado}):
Responde EXCLUSIVAMENTE con un objeto JSON válido que siga exactamente esta estructura:
${schema}

REGLAS DEL JSON ESTRUCTURADO:
- Responde únicamente con JSON válido, sin Markdown ni bloques de código.
- No agregues ningún texto antes ni después del JSON.
- La propiedad "tipo" debe ser exactamente "${tipoEsperado}".
- Usa arrays vacíos [] cuando no haya elementos; nunca uses null.
- Si el contenido disponible no es suficiente, devuelve igualmente la estructura y describe el faltante en el array "advertencias".${STRUCTURED_EXTRA_RULES[tipoEsperado] ? '\n\n' + STRUCTURED_EXTRA_RULES[tipoEsperado] : ''}`;
}

/**
 * Construye el prompt (system + user) para una Quick Action genérica, usando
 * el contenido obtenido de Documento IA (o el contexto disponible) como fuente.
 * @param {Object} params
 * @param {Object} params.action - Quick Action (hardcodeada o dinámica)
 * @param {string} params.contextoTexto - Contenido disponible (lección o libro)
 * @param {Object|null} [params.documentoIA] - Respuesta cruda de Documento IA (solo para metadata)
 * @param {string} [params.userRole] - Rol del usuario actual
 * @param {'lesson'|'book'} [params.tipoVista]
 * @param {'text'|'audio'|'podcast'|'image'|'diagram'} [params.responseType]
 * @param {string} [params.tituloContenido]
 * @param {string} [params.nombreAccion]
 * @returns {{ system: string, user: string, metadata: Object }}
 */
export function buildActionPrompt({
  action,
  contextoTexto,
  documentoIA = null,
  userRole = 'general',
  tipoVista = 'lesson',
  responseType = 'text',
  tituloContenido = '',
  nombreAccion = '',
  conversationHistory = [],
  totalLecciones = 0,
  instruccionAdicional = ''
}) {
  const editorInstruction = getEditorInstruction(action, tipoVista);
  if (!editorInstruction) {
    console.warn('[PromptBuilder] No se encontró prompt editorial para la acción', action?.actionId || action?.id || action?.label);
  }

  const outputRules = getOutputRules(responseType, tipoVista);

  // Para salidas estructuradas, agregar el schema JSON específico del structuredType.
  const structuredType = responseType === 'structured' ? getStructuredType(action) : '';
  const structuredSchemaRules = responseType === 'structured'
    ? getStructuredSchemaRules(structuredType)
    : '';

  const securityRules = `REGLAS OBLIGATORIAS:
- Usa únicamente el contenido proporcionado.
- No inventes información.
- La metadata del contenido (identificador, campos curriculares, autores, idioma, recursos digitales, materiales) es solo referencia interna: NUNCA la reproduzcas ni la muestres como parte de la respuesta.
- Si el contenido es escaso, entrega lo que sí se pueda con el contenido disponible y anota lo que falte; no rechaces la tarea ni pidas al usuario que elija otro libro o lección.
- Respeta el rol del usuario.`;

  const system = [
    buildSystemPrompt(userRole, 'generic', {}, responseType, editorInstruction || null),
    outputRules,
    structuredSchemaRules,
    securityRules
  ].filter(Boolean).join('\n\n');

  const tipoContenido = tipoVista === 'book' ? 'LIBRO' : 'LECCIÓN';
  const accionSolicitada = nombreAccion || action?.label || editorInstruction || action?.actionId || action?.id || '';

  // Decirle cuántas lecciones trae el contenido evita que agrupe o se salte parte:
  // con títulos repetidos (7 sesiones del mismo proyecto) tiende a fusionarlas.
  const bloqueLecciones = totalLecciones > 0
    ? `\nLECCIONES INCLUIDAS: ${totalLecciones} (una por cada lección numerada del contenido; cúbrelas todas, en ese orden, conservando su número)`
    : '';
  const bloqueAdicional = instruccionAdicional ? `\n${instruccionAdicional}` : '';

  let user = `TIPO DE CONTENIDO: ${tipoContenido}
TÍTULO: ${tituloContenido}
ACCIÓN SOLICITADA: ${accionSolicitada}${bloqueLecciones}${bloqueAdicional}

CONTENIDO DISPONIBLE:
${contextoTexto || ''}`;

  // Historial reciente (solo lo traen las Quick Actions del área de chat). Mismo
  // formato y ventana (últimos 3) que sendChatMessage.
  if (conversationHistory.length > 0) {
    const historyText = conversationHistory.slice(-3)
      .map(msg => (msg.role === 'user' ? 'Usuario' : 'Asistente') + ': ' + msg.content)
      .join('\n');
    user = 'HISTORIAL RECIENTE DE LA CONVERSACIÓN:\n' + historyText + '\n\n' + user;
  }

  const metadata = {
    tipoVista,
    responseType,
    structuredType: structuredType || null,
    userRole,
    actionId: action?.actionId || action?.id || null,
    actionLabel: action?.label || null,
    hasEditorInstruction: Boolean(editorInstruction),
    hasDocumentoIA: Boolean(documentoIA),
    contextoLength: contextoTexto?.length || 0,
    totalLecciones
  };

  return { system, user, metadata };
}

// ---------------------------------------------------------------------------
// Validación de relevancia (pre-check antes de llamar al chat principal)
// ---------------------------------------------------------------------------

// Palabras que indican que la pregunta es sobre el contenido educativo actual — siempre relevante
const META_KEYWORDS = ['lección', 'leccion', 'libro', 'tema', 'capítulo', 'capitulo', 'contenido',
  'explica', 'explícame', 'explicame', 'dime', 'cuéntame', 'cuentame', 'contexto',
  'resumen', 'resume', 'describe', 'qué es', 'que es', 'cómo', 'como', 'más sobre',
  'mas sobre', 'información', 'informacion', 'aprend', 'enseña', 'ensena'];

/**
 * Verifica si el mensaje del usuario es relevante para el tema dado.
 * Retorna true si es relevante o si no se puede determinar (beneficio de la duda).
 */
async function isMessageRelevant(userMessage, topic) {
  const msgLower = userMessage.toLowerCase();

  // Preguntas meta sobre "la lección / el libro / el tema" siempre son relevantes
  if (META_KEYWORDS.some(kw => msgLower.includes(kw))) return true;

  try {
    const data = {
      systemPrompt: 'Eres un clasificador de preguntas educativas. Responde ÚNICAMENTE con "SI" o "NO", sin explicación.',
      userPrompt: `Contexto: el usuario está estudiando el tema educativo "${topic}".\n¿La siguiente pregunta es completamente ajena a ese tema (por ejemplo: una receta, un chiste, algo sin relación educativa)?\n\nPregunta: "${userMessage}"\n\nResponde SI si es completamente ajena, NO si puede tener alguna relación.`,
      options: { temperature: 0, maxTokens: 5 }
    };
    const response = await openAICall(data, PRODUCTO_NIA);
    if (response.error) return true;
    const answer = (response.data?.data?.content || '').trim().toUpperCase();
    // La pregunta ES del clasificador "¿es ajena?" — si responde SI, rechazamos
    return !answer.startsWith('SI');
  } catch {
    return true;
  }
}

// ---------------------------------------------------------------------------
// sendChatMessage
// ---------------------------------------------------------------------------

/**
 * Envía un mensaje al chatbot con contexto real del libro o la lección.
 * @param {string} userMessage
 * @param {Array} conversationHistory
 * @param {string} userRole - valor de ROLES (src/components/utils/roles.js) o 'general' como fallback
 * @param {Object|null} lessonContext - { _id, titulo } de la lección seleccionada
 * @param {Object|null} lessonUMAData - UMA completa obtenida de la API
 * @param {{ titulo: string, lecciones: string[] }|null} bookData - datos ligeros del libro actual
 */
export async function sendChatMessage(
  userMessage,
  conversationHistory = [],
  userRole = 'general',
  lessonContext = null,
  lessonUMAData = null,
  bookData = null,
  model = 'gpt-4.1-mini',
  isAudioResponse = false,
  isPodcast = false,
  umaId = null,
  leccionesData = []
) {
  try {
    const currentLessonName = lessonContext?.titulo || null;
    const responseType = isPodcast ? 'podcast' : isAudioResponse ? 'audio' : 'text';

    // --- Validar relevancia del mensaje ---
    const topic = currentLessonName || bookData?.titulo;
    if (topic) {
      const relevant = await isMessageRelevant(userMessage, topic);
      if (!relevant) {
        return `Solo puedo ayudarte con preguntas relacionadas con "${topic}". Por favor, haz una pregunta sobre los temas de este contenido.`;
      }
    }

    // --- Determinar contexto y system prompt ---
    let contextType, contextData;
    let searchResult = { found: false };

    if (lessonUMAData && currentLessonName) {
      contextType = 'lesson';
      contextData = { lessonName: currentLessonName, uma: lessonUMAData };
      searchResult = extractContextFromUMA(lessonUMAData, userMessage);
    } else if (bookData) {
      contextType = 'book';
      contextData = { bookData };
    } else {
      contextType = 'generic';
      contextData = {};
    }

    let enhancedSystemPrompt = buildSystemPrompt(userRole, contextType, contextData, responseType);

    // --- Contexto principal: Documento IA (misma fuente que los quick actions) ---
    // En lección: 1 llamada con el umaId. En libro: una por lección y se combinan.
    // Si no se obtiene nada, se cae a la extracción local de la UMA / bookData.
    let documentoIAContext = '';
    try {
      if (contextType === 'lesson' && umaId) {
        const { contextoTexto } = await getDocumentoIA({ umaId, rol: userRole });
        if (contextoTexto) documentoIAContext = contextoTexto;
      } else if (contextType === 'book' && leccionesData.length > 0) {
        const partes = [];
        for (const lec of leccionesData.filter(l => l.id)) {
          const titulo = lec.agrupadores?.length ? `${lec.agrupadores.join(' > ')} > ${lec.nombre}` : lec.nombre;
          try {
            const { contextoTexto } = await getDocumentoIA({ umaId: lec.id, rol: userRole });
            if (contextoTexto) partes.push(`===== INICIO LECCIÓN: ${titulo} =====\n${contextoTexto}\n===== FIN LECCIÓN: ${titulo} =====`);
          } catch { /* lección sin contexto: se omite */ }
        }
        if (partes.length > 0) documentoIAContext = partes.join('\n\n---\n\n');
      }
    } catch (err) {
      console.warn('[ChatbotLibro] Documento IA no disponible para el chat libre, usando fallback local:', err?.message);
    }

    // --- Inyectar contexto de contenido si existe ---
    let finalPrompt = userMessage;

    if (documentoIAContext) {
      enhancedSystemPrompt += '\n\nINFORMACIÓN DEL CONTENIDO (Documento IA):\n' + documentoIAContext + '\n\nIMPORTANTE: Usa esta información como base principal.';
      finalPrompt = 'El usuario pregunta sobre: ' + userMessage;
    } else if (searchResult.found && searchResult.data.length > 0) {
      let contextInfo = '\n\nINFORMACIÓN ENCONTRADA EN LA LECCIÓN:\n';

      for (const resultado of searchResult.data) {
        contextInfo += '\n--- Lección: ' + resultado.leccion + ' ---\n';
        if (resultado.objetivo) contextInfo += 'Objetivo: ' + resultado.objetivo + '\n';
        if (resultado.tipoBloque) contextInfo += 'Tipo de contenido: ' + resultado.tipoBloque + '\n';
        if (resultado.contenido) contextInfo += 'Contenido:\n' + resultado.contenido + '\n';
        if (resultado.esModoProfesor) contextInfo += '(Contenido para profesores)\n';
        if (resultado.tieneImagen) contextInfo += 'Este bloque incluye imagen ilustrativa.\n';
        if (resultado.tieneVideo) contextInfo += 'Este bloque incluye video.\n';
        if (resultado.tiposBloques?.length > 0) {
          contextInfo += 'Tipos de contenido: ' + resultado.tiposBloques.join(', ') + '\n';
        }
      }

      contextInfo += '\nIMPORTANTE: Usa esta información como base principal.';
      enhancedSystemPrompt += contextInfo;
      finalPrompt = 'El usuario pregunta sobre: ' + userMessage;
    } else if (contextType === 'lesson') {
      enhancedSystemPrompt += `\n\nNOTA: No se encontró contenido específico. Usa tu conocimiento general sobre "${currentLessonName}".`;
    }

    // --- Historial reciente ---
    if (conversationHistory.length > 0) {
      const recentHistory = conversationHistory.slice(-3);
      const historyText = recentHistory
        .map(msg => (msg.role === 'user' ? 'Usuario' : 'Asistente') + ': ' + msg.content)
        .join('\n');
      finalPrompt = 'Historial:\n' + historyText + '\n\n' + finalPrompt;
    }

    const response = await openAICall({
      systemPrompt: enhancedSystemPrompt,
      userPrompt: finalPrompt,
      // Temperatura baja: el chat libre es una tarea de localizar/citar contenido,
      // no de creatividad. Reduce atribuciones y respuestas inventadas.
      options: { temperature: 0.2, maxTokens: getMaxTokens(model, responseType), model }
    }, PRODUCTO_NIA);

    if (response.error) {
      console.error('[ChatbotLibro] Error:', response.error);
      return 'Lo siento, tuve un problema técnico. ¿Puedes intentar de nuevo?';
    }

    return response.data.data.content;
  } catch (error) {
    console.error('[ChatbotLibro] Error:', error);
    return 'Lo siento, estoy teniendo dificultades. Por favor, intenta de nuevo.';
  }
}

// ---------------------------------------------------------------------------
// generateDiagramCode
// ---------------------------------------------------------------------------

/**
 * Genera código de diagrama usando OpenAI.
 * @param {string} diagramType
 * @param {string} userQuery - Instrucción (SOLICITUD) de qué diagrama generar
 * @param {Object|null} lessonUMAData - UMA real para contexto
 * @param {{ titulo: string, lecciones: string[] }|null} bookData - datos del libro para contexto
 * @param {string} [model]
 * @param {string|null} [contentOverride] - Contenido fuente ya curado (ej. responseContent de
 *   Documento IA). Si se pasa, reemplaza el contexto extraído de lessonUMAData/bookData.
 */
export async function generateDiagramCode(diagramType, userQuery, lessonUMAData = null, bookData = null, model = 'gpt-4.1-mini', contentOverride = null) {
  try {
    let bookContent;
    if (typeof contentOverride === 'string' && contentOverride.trim()) {
      // Contenido ya curado por Documento IA + buildActionPrompt (Quick Actions de diagrama).
      // userQuery sigue siendo la instrucción (ej. "Genera un mapa mental de la lección X").
      bookContent = contentOverride;
    } else if (lessonUMAData) {
      bookContent = getContentFromUMA(lessonUMAData);
    } else if (bookData) {
      bookContent = getBookContentForImage(bookData);
    } else {
      bookContent = 'contenido educativo';
    }

    const { generateDiagramPrompt } = await import('./diagramService');
    const diagramPrompt = generateDiagramPrompt(diagramType, userQuery, bookContent);

    const data = {
      systemPrompt: `Eres un experto en crear diagramas y mapas mentales.
Tu tarea es generar código válido para visualizaciones.
REGLAS ESTRICTAS para código Mermaid:
- NUNCA uses comillas dobles (") dentro de etiquetas de nodos como [texto], (texto) o {texto}
- Usa comillas simples (') si necesitas citar algo dentro de una etiqueta
- Mantén las etiquetas de nodos concisas y sin caracteres especiales problemáticos
IMPORTANTE: Responde ÚNICAMENTE con el código, sin explicaciones ni bloques de código markdown.`,
      userPrompt: diagramPrompt,
      options: { temperature: 0.7, maxTokens: getMaxTokens(model, 'diagram'), model }
    };

    const response = await openAICall(data, PRODUCTO_NIA);

    if (response.error) {
      console.error('[ChatbotLibro] Error generando diagrama:', response.error);
      return { code: null, error: 'Error al generar el diagrama' };
    }

    return { code: response.data.data.content, error: null };
  } catch (error) {
    console.error('[ChatbotLibro] Error en generateDiagramCode:', error);
    return { code: null, error: error.message || 'Error al generar el diagrama' };
  }
}

// ---------------------------------------------------------------------------
// Documento IA — Resumen en texto
// ---------------------------------------------------------------------------

/**
 * @deprecated Wrapper de compatibilidad. Usa buildActionPrompt() directamente.
 * Construye los prompts system/user para el resumen completo del libro,
 * combinando el contextoTexto de todas las lecciones obtenido por Documento IA.
 */
function buildResumenLibroPrompt(contextoTotal, tituloLibro, action, userRole) {
  //console.log("Acción: ", action);
  const { system, user } = buildActionPrompt({
    action,
    contextoTexto: contextoTotal,
    userRole,
    tipoVista: 'book',
    responseType: 'text',
    tituloContenido: tituloLibro,
    nombreAccion: action.label || action.actionId || 'Resumen de libro'
  });
  return { system, user };
}

/**
 * @deprecated Wrapper de compatibilidad. Usa buildActionPrompt() directamente.
 * Construye los prompts system/user para la acción de Resumen en texto usando
 * el contextoTexto proveniente del endpoint de Documento IA.
 */
function buildResumenPrompt(contextoTexto, action, userRole, tituloContenido = '') {
  const { system, user } = buildActionPrompt({
    action,
    contextoTexto,
    userRole,
    tipoVista: 'lesson',
    responseType: 'text',
    tituloContenido,
    nombreAccion: action.label || action.actionId || 'Resumen de lección'
  });
  return { system, user };
}

/**
 * Cuenta las lecciones que una respuesta cubre de verdad: un encabezado Markdown
 * por lección. Sirve para detectar que el modelo agrupó — con títulos repetidos
 * ("Contextualizo" en cada unidad) tiende a fusionar las que se llaman igual.
 * @param {string} contenido
 * @returns {number}
 */
function contarLeccionesCubiertas(contenido) {
  const texto = String(contenido || '');
  // Las lecciones van numeradas y los agrupadores no, así que los encabezados con
  // número son la cuenta buena.
  const numerados = (texto.match(/^#{2,6}\s+\d+[.)]/gm) || []).length;
  if (numerados > 0) return numerados;
  // Si el modelo no numeró, se cuentan todos los encabezados: sobreestima, pero
  // evita reintentar en vano hasta agotar las particiones.
  return (texto.match(/^#{2,6} .+$/gm) || []).length;
}

/**
 * Llama a OpenAI para una Quick Action y avisa si la respuesta se cortó por tope
 * de salida. OpenAI NO devuelve error en ese caso: manda el texto truncado a media
 * frase con finish_reason 'length'. El backend sí reenvía usage.completion_tokens,
 * así que se compara contra el máximo pedido; sin esto, una respuesta cortada se
 * muestra tal cual y —peor— se guarda en caché para todo el rol.
 * @returns {Promise<{ content: string|null, truncado: boolean, error: any }>}
 */
async function llamarOpenAIQuickAction({ system, user, model, maxTokensKey, temperature = 0.7 }) {
  const maxTokens = getMaxTokens(model, maxTokensKey);
  const respuesta = await openAICall({
    systemPrompt: system,
    userPrompt: user,
    options: { temperature, maxTokens, model }
  }, PRODUCTO_NIA);

  if (respuesta.error) return { content: null, truncado: false, error: respuesta.error };

  const datos = respuesta.data?.data || {};
  const usados = datos.usage?.completion_tokens ?? 0;
  const truncado = usados > 0 && usados >= maxTokens;
  if (truncado) {
    console.warn('[ChatbotLibro] Respuesta cortada por el tope de salida del modelo', {
      model, maxTokens, completion_tokens: usados,
    });
  }
  return { content: datos.content, truncado, error: null };
}

/**
 * Ejecuta una Quick Action (de lección) usando Documento IA como fuente de
 * contexto y el PromptBuilder genérico (buildActionPrompt). Si Documento IA
 * o la llamada a OpenAI fallan, hace fallback a sendChatMessage.
 * @returns {Promise<{ type: string, content: string, metadata: Object }>}
 */
async function executeLessonActionWithDocumentoIA({
  action,
  responseType,
  umaId,
  rol,
  chatRole = 'general',
  conversationHistory = [],
  lessonContext = null,
  lessonUMAData = null,
  bookData = null,
  model = 'gpt-4.1-mini'
}) {
  const actionId = action.actionId || action.id || null;

  if (umaId) {
    try {
      const { documentoIA, contextoTexto } = await getDocumentoIA({ umaId, rol });
      //console.log('[DocumentoIA] Documento de la lección completo →', contextoTexto);

      const { system, user } = buildActionPrompt({
        action,
        contextoTexto,
        documentoIA,
        userRole: chatRole,
        tipoVista: 'lesson',
        responseType,
        conversationHistory,
        tituloContenido: lessonContext?.titulo || documentoIA?.titulo || ''
      });
      const { content, truncado, error } = await llamarOpenAIQuickAction({
        system, user, model, maxTokensKey: getMaxTokensKey(responseType, action)
      });
      if (!error) {
        return { type: responseType, content, metadata: { actionId, usedDocumentoIA: true, truncado } };
      }
    } catch (err) {
      console.warn('[DocumentoIA] Fallback a contexto anterior:', err);
    }
  }

  const fallbackQuery = action.queryLesson || action.query || action.label;
  const content = await sendChatMessage(
    fallbackQuery,
    conversationHistory,
    chatRole,
    lessonContext,
    lessonUMAData,
    bookData,
    model,
    responseType === 'audio' || responseType === 'podcast',
    responseType === 'podcast'
  );

  return { type: responseType, content, metadata: { actionId, usedDocumentoIA: false, fallback: true } };
}

/**
 * Ejecuta una Quick Action (de libro) llamando a getDocumentoIA por cada
 * lección y combinando el contexto. Si no hay lecciones con contexto, o si
 * Documento IA / OpenAI fallan, hace fallback a sendChatMessage.
 * @returns {Promise<{ type: string, content: string, metadata: Object }>}
 */
async function executeBookActionWithDocumentoIA({
  action,
  responseType,
  bookData,
  leccionesData = [],
  rol,
  chatRole = 'general',
  conversationHistory = [],
  model = 'gpt-4.1-mini'
}) {
  const actionId = action.actionId || action.id || null;
  const fallbackQuery = action.queryBook || action.query || action.label;

  // El fallback debe recibir `leccionesData` (10º y 11º argumento de sendChatMessage);
  // sin ellos la rama de libro no carga Documento IA y el prompt sale sin contenido.
  const fallback = (motivo) => {
    console.warn(`[ChatbotLibro] Resumen de libro sin Documento IA (${motivo}); respuesta degradada.`);
    return sendChatMessage(
      fallbackQuery, conversationHistory, chatRole, null, null, bookData, model,
      responseType === 'audio' || responseType === 'podcast',
      responseType === 'podcast',
      null,
      leccionesData
    ).then(content => ({ type: responseType, content, metadata: { actionId, usedDocumentoIA: false, fallback: true } }));
  };

  const leccionesConId = leccionesData.filter(l => l.id);

  // DEBUG: todas las lecciones del libro que llegan al resumen (las que no tienen id no se consultan)
  /* console.log('[Resumen libro] Lecciones del libro →', {
    total: leccionesData.length,
    conId: leccionesConId.length,
    sinId: leccionesData.length - leccionesConId.length,
    lecciones: leccionesData.map(l => ({
      id: l.id || null,
      nombre: l.nombre,
      agrupadores: l.agrupadores || [],
    })),
  }); */

  if (leccionesConId.length === 0) return fallback(`el libro no tiene lecciones con umaId (recibidas: ${leccionesData.length})`);

  const estadoLecciones = []; // DEBUG: por qué cada lección entra o se omite del resumen

  // Documento IA en paralelo con cupo fijo: en secuencia, un libro de 153 lecciones
  // encadena 153 consultas pesadas (obtenerPorId popula todo el árbol de la UMA) y el
  // usuario espera minutos antes de la primera llamada a la IA. El cupo evita
  // dispararle al backend las 153 de golpe. Se conserva el orden del libro: la
  // numeración de las lecciones depende de él.
  const CONSULTAS_SIMULTANEAS = 5;
  const resultadosContexto = new Array(leccionesConId.length).fill(null);
  let siguiente = 0;
  const trabajador = async () => {
    while (siguiente < leccionesConId.length) {
      const indice = siguiente++;
      const leccion = leccionesConId[indice];
      const agrupador = leccion.agrupadores?.length ? leccion.agrupadores.join(' > ') : '';
      const titulo = agrupador ? `${agrupador} > ${leccion.nombre}` : leccion.nombre;
      try {
        const { contextoTexto } = await getDocumentoIA({ umaId: leccion.id, rol });
        if (contextoTexto) {
          resultadosContexto[indice] = { nombre: leccion.nombre, agrupador, contextoTexto };
          estadoLecciones.push({ titulo, estado: 'incluida', contextoLength: contextoTexto.length });
        } else {
          estadoLecciones.push({ titulo, estado: 'sin-contexto', contextoLength: 0 });
        }
      } catch (err) {
        estadoLecciones.push({ titulo, estado: 'error', error: err.message });
        console.warn(`[DocumentoIA] Sin contexto para "${leccion.nombre}" (${leccion.id}):`, err.message);
      }
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(CONSULTAS_SIMULTANEAS, leccionesConId.length) }, trabajador)
  );

  const contextos = resultadosContexto
    .filter(Boolean)
    .map((leccion, indice) => {
      const cabecera = `## ${indice + 1}. ${leccion.nombre}`;
      const linea = leccion.agrupador ? `\nAgrupador: ${leccion.agrupador}` : '';
      return `${cabecera}${linea}\n${leccion.contextoTexto}`;
    });

  // DEBUG: cuáles lecciones entraron al contexto del resumen y cuáles se omitieron
  /* console.log('[Resumen libro] Estado por lección →', {
    incluidas: contextos.length,
    omitidas: estadoLecciones.filter(e => e.estado !== 'incluida').length,
    detalle: estadoLecciones,
  }); */

  if (contextos.length === 0) {
    const errores = estadoLecciones.filter(e => e.estado === 'error').length;
    const sinContexto = estadoLecciones.filter(e => e.estado === 'sin-contexto').length;
    return fallback(`ninguna de las ${leccionesConId.length} lecciones devolvió contextoTexto — ${errores} con error, ${sinContexto} sin contexto`);
  }

  // El resumen de libro requiere más tokens que una respuesta de texto normal.
  const maxTokensKey = responseType === 'text' ? 'bookSummary' : getMaxTokensKey(responseType, action);

  // Libros largos: una sola llamada acaba agrupando lecciones ("Proyectos de aula
  // para la NEM 2°", 153 lecciones → 21 secciones y finish_reason 'stop': el modelo
  // no se corta, decide fusionar). Se procesa por lotes y se concatena, así ninguna
  // lección se pierde. Solo aplica a texto y solo si la tarea es por lección.
  const LECCIONES_POR_LOTE = 35;
  // Tope de entrada por lote (~30k tokens). El conteo de lecciones por sí solo no
  // basta: 35 lecciones muy largas desbordarían la ventana del modelo y OpenAI
  // respondería 400 (context_length_exceeded) o 429 si supera el TPM de la cuenta.
  const MAX_CHARS_POR_LOTE = 120000;
  // Entra aquí cualquier resumen por lección, tenga el libro 20 o 200: es la ruta
  // que verifica la cobertura y reintenta. Con una sola llamada, un libro con
  // títulos repetidos se colapsaba (22 lecciones → 13 entradas) sin que nadie lo
  // notara.
  if (responseType === 'text'
    && esTareaPorLeccion(getEditorInstruction(action, 'book'))) {
    // Una lección sola nunca debe pasarse del presupuesto del lote.
    const contextosAcotados = contextos.map(contexto => (
      contexto.length > MAX_CHARS_POR_LOTE
        ? `${contexto.slice(0, MAX_CHARS_POR_LOTE)}\n[…contenido recortado por longitud…]`
        : contexto
    ));

    const lotes = [];
    let loteActual = [];
    let charsActuales = 0;
    for (const contexto of contextosAcotados) {
      const cierraPorTamano = loteActual.length > 0 && charsActuales + contexto.length > MAX_CHARS_POR_LOTE;
      const cierraPorConteo = loteActual.length >= LECCIONES_POR_LOTE;
      if (cierraPorTamano || cierraPorConteo) {
        lotes.push(loteActual);
        loteActual = [];
        charsActuales = 0;
      }
      loteActual.push(contexto);
      charsActuales += contexto.length;
    }
    if (loteActual.length > 0) lotes.push(loteActual);

    // Si un lote sale cortado por el tope de salida, se parte en dos y se reintenta:
    // el contenido no cambia, solo se reparte en más respuestas. Dos niveles bastan
    // (un lote de 35 lecciones baja a ~9), y el corte no vuelve a intentarse cuando
    // el lote ya es de una sola lección: ahí el tope no da para más.
    const MAX_PARTICIONES = 2;
    let particiones = 0;

    const pedirLote = async (lote, esPrimero, profundidad = 0) => {
      const unicoBloque = esPrimero && lote.length === contextos.length;
      const instruccionAdicional = unicoBloque
        ? ''
        : esPrimero
          ? `Este es el primer bloque de lecciones del libro: habrá más lecciones después, así que no escribas conclusión ni cierre.`
          : `Este bloque continúa la misma lista de lecciones. NO repitas el título general del libro ni escribas introducción o conclusión; empieza directamente con la primera lección de este bloque.`;

      const { system, user } = buildActionPrompt({
        action,
        contextoTexto: lote.join('\n\n---\n\n'),
        documentoIA: null,
        userRole: chatRole,
        tipoVista: 'book',
        responseType,
        conversationHistory,
        tituloContenido: bookData.titulo,
        totalLecciones: lote.length,
        instruccionAdicional,
      });

      const { content, truncado, error } = await llamarOpenAIQuickAction({
        system, user, model, maxTokensKey
      });
      if (error) return null;

      // Dos formas de quedarse corto: que la respuesta se corte por el tope de
      // salida, o que el modelo agrupe lecciones y devuelva menos entradas.
      const cubiertas = contarLeccionesCubiertas(content);
      const agrupado = cubiertas < lote.length;
      if ((!truncado && !agrupado) || lote.length < 2 || profundidad >= MAX_PARTICIONES) {
        return { content, truncado: truncado || agrupado };
      }

      // Reintento: mismas lecciones, repartidas en dos respuestas.
      particiones++;
      const corte = Math.ceil(lote.length / 2);
      console.warn('[ChatbotLibro] Lote incompleto, se reintenta partido en dos', {
        lecciones: lote.length, cubiertas, motivo: truncado ? 'cortado por tope de salida' : 'el modelo agrupó',
        profundidad: profundidad + 1,
      });
      const mitades = await Promise.all([
        pedirLote(lote.slice(0, corte), esPrimero, profundidad + 1).catch(() => null),
        pedirLote(lote.slice(corte), false, profundidad + 1).catch(() => null),
      ]);
      const logradas = mitades.filter(Boolean);
      if (logradas.length === 0) return { content, truncado: true }; // se conserva la cortada
      return {
        content: logradas.map(m => m.content).join('\n\n'),
        truncado: logradas.length < 2 || logradas.some(m => m.truncado),
      };
    };

    const resultados = await Promise.all(
      lotes.map((lote, indice) => pedirLote(lote, indice === 0).catch(() => null))
    );
    const utiles = resultados.filter(Boolean);

    if (utiles.length > 0) {
      const lotesFallidos = lotes.length - utiles.length;
      const truncado = utiles.some(r => r.truncado);
      const contenido = utiles.map(r => r.content).join('\n\n');
      // Cobertura real: se cuenta un encabezado por lección. Si salen menos, el
      // resumen quedó incompleto (un lote falló, se cortó o el modelo agrupó) y no
      // debe guardarse en caché: quedaría incompleto para todo el rol.
      const encabezados = contarLeccionesCubiertas(contenido);
      const incompleto = lotesFallidos > 0 || truncado || encabezados < contextos.length;

      if (incompleto) {
        console.warn('[ChatbotLibro] Resumen de libro incompleto', {
          lecciones: contextos.length, encabezados, lotes: lotes.length, lotesFallidos, truncado, particiones,
        });
      }
      return {
        type: responseType,
        content: contenido,
        metadata: {
          actionId, usedDocumentoIA: true,
          lecciones: contextos.length, encabezados,
          lotes: lotes.length, lotesFallidos, particiones, truncado, incompleto,
        }
      };
    }
    return fallback(`los ${lotes.length} lotes del resumen fallaron en OpenAI`);
  }

  // Tope del contexto del libro. Un libro grande (p. ej. 175 lecciones ≈ 165k tokens)
  // desborda la ventana del modelo: OpenAI devuelve error, se cae al fallback sin
  // contenido y el usuario ve un "no puedo darte un resumen de cada lección". Se
  // recorta por lección, en partes iguales, para que TODAS sigan representadas.
  const MAX_CHARS_CONTEXTO_LIBRO = 240000; // ~60k tokens, holgado frente a la ventana de gpt-4.1
  let contextoTotal = contextos.join('\n\n---\n\n');
  if (contextoTotal.length > MAX_CHARS_CONTEXTO_LIBRO) {
    const presupuestoPorLeccion = Math.max(800, Math.floor(MAX_CHARS_CONTEXTO_LIBRO / contextos.length));
    const recortados = contextos.map(contexto => (
      contexto.length > presupuestoPorLeccion
        ? `${contexto.slice(0, presupuestoPorLeccion)}\n[…contenido recortado por longitud…]`
        : contexto
    ));
    console.warn('[ChatbotLibro] Contexto del libro recortado', {
      lecciones: contextos.length,
      charsOriginal: contextoTotal.length,
      charsPorLeccion: presupuestoPorLeccion,
    });
    contextoTotal = recortados.join('\n\n---\n\n');
  }

  const { system, user } = buildActionPrompt({
    action,
    contextoTexto: contextoTotal,
    documentoIA: null,
    userRole: chatRole,
    tipoVista: 'book',
    responseType,
    conversationHistory,
    tituloContenido: bookData.titulo,
    totalLecciones: contextos.length
  });

  const { content, truncado, error } = await llamarOpenAIQuickAction({
    system, user, model, maxTokensKey
  });

  if (!error) {
    return {
      type: responseType,
      content,
      metadata: { actionId, usedDocumentoIA: true, lecciones: contextos.length, truncado, incompleto: truncado }
    };
  }
  return fallback(`OpenAI devolvió error con ${contextos.length} lecciones de contexto`);
}

/**
 * Ejecutor genérico de Quick Actions: dada cualquier acción (creada desde el
 * Gestor o hardcodeada), resuelve su outputType y genera el contenido usando
 * Documento IA + buildActionPrompt, ya sea para lección o para libro.
 * @returns {Promise<{ type: string, content: string, metadata: Object }>}
 */
export async function executeQuickActionWithDocumentoIA({
  action,
  umaId,
  rol,
  chatRole = 'general',
  isBookView = false,
  conversationHistory = [],
  lessonContext = null,
  lessonUMAData = null,
  bookData = null,
  leccionesData = [],
  model = 'gpt-4.1-mini'
}) {
  const responseType = getActionOutputType(action);

  if (isBookView) {
    return executeBookActionWithDocumentoIA({ action, responseType, bookData, leccionesData, rol, chatRole, conversationHistory, model });
  }

  return executeLessonActionWithDocumentoIA({
    action, responseType, umaId, rol, chatRole, conversationHistory,
    lessonContext, lessonUMAData, bookData, model
  });
}

/**
 * @deprecated Usa executeQuickActionWithDocumentoIA() directamente.
 * Genera el resumen en texto usando Documento IA como fuente de contexto.
 */
export async function sendResumenConDocumentoIA({
  umaId,
  rol,
  action,
  conversationHistory = [],
  chatRole = 'general',
  lessonContext = null,
  lessonUMAData = null,
  bookData = null,
  model = 'gpt-4.1-mini'
}) {
  const { content } = await executeLessonActionWithDocumentoIA({
    action, responseType: 'text', umaId, rol, chatRole, conversationHistory,
    lessonContext, lessonUMAData, bookData, model
  });
  return content;
}

/**
 * @deprecated Usa executeQuickActionWithDocumentoIA() directamente.
 * Genera el resumen completo de un libro llamando a getDocumentoIA por cada lección.
 */
export async function sendResumenLibroConDocumentoIA({
  bookData,
  leccionesData = [],
  rol,
  action,
  chatRole = 'general',
  model = 'gpt-4.1-mini'
}) {
  const { content } = await executeBookActionWithDocumentoIA({
    action, responseType: 'text', bookData, leccionesData, rol, chatRole, model
  });
  return content;
}

// Wrappers de compatibilidad — exportados para pruebas/migraciones futuras
export { buildResumenPrompt, buildResumenLibroPrompt };

export default {
  sendChatMessage,
  getBookContentForImage,
  generateDiagramCode,
  generateSystemPrompt: buildSystemPrompt,
  getActionOutputType,
  getStructuredType,
  getActionModel,
  DEFAULT_MODEL,
  executeQuickActionWithDocumentoIA,
  esRespuestaRechazada,
  sendResumenConDocumentoIA,
  sendResumenLibroConDocumentoIA,
  buildActionPrompt,
  buildResumenPrompt,
  buildResumenLibroPrompt
};
